import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { TextField, Autocomplete, createFilterOptions, Skeleton } from "@mui/material";
import type { Perimetre } from "models";
import { perimetresService } from "services";
import { useErrorHandler, withComponentErrorBoundary } from "utils/errorHandling";
import { stringUtil } from "@sdeapps/react-core";
import { PerimetreBox } from "components/Business";
import { sortingUtil } from "utils";

const filterOptions = createFilterOptions({
  trim: true,
  stringify: (option: Perimetre) => {
    const normalizedString = stringUtil.normalize(
      `${option.competence} ${option.code} ${option.libelle}`
    );
    return `${normalizedString} ${option.libelle}`;
  },
});

interface PerimetreAutoCompleteProps {
  value: Perimetre | null;
  onChange?: (perimetre: Perimetre | null) => void;
  label?: string;
  placeholder?: string;
  size?: "small" | "medium";
  disabled?: boolean;
}

function PerimetreAutoComplete({
  value,
  onChange,
  label,
  placeholder,
  size = "medium",
  disabled = false,
}: Readonly<PerimetreAutoCompleteProps>): ReactNode {
  const [input, setInput] = useState<string>("");
  const [perimetres, setPerimetres] = useState<Array<Perimetre>>([]);
  const { catchErrors, isLoading } = useErrorHandler();

  useEffect(() => {
    async function getPerimetres(): Promise<void> {
      const _perimetres = await perimetresService.getAll();
      _perimetres.sort(sortingUtil.sortPerimetres);
      setPerimetres(_perimetres);
    }

    void catchErrors(getPerimetres);
  }, [catchErrors]);

  return (
    <Autocomplete
      value={perimetres.find((c) => c.id === value?.id) ?? value ?? null}
      onChange={(_e, _value: Perimetre | null) => {
        onChange?.(_value);
      }}
      onInputChange={(_e, inputValue) => {
        setInput(inputValue);
      }}
      options={perimetres}
      filterOptions={filterOptions}
      fullWidth
      autoHighlight
      openOnFocus
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      size={size}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      getOptionLabel={({ libelle }) => libelle}
      renderInput={(params) => <TextField {...params} label={label} placeholder={placeholder} />}
      renderValue={(perimetre) => input === "" && <PerimetreBox perimetre={perimetre} noWrap />}
      getOptionKey={({ id }) => id}
      renderOption={(props, perimetre) => (
        <li {...props} key={perimetre.id}>
          <PerimetreBox perimetre={perimetre} noWrap />
        </li>
      )}
      // Vide l'input si l'utilisateur appuie sur Retour Arrière
      onKeyDown={(e) => {
        if (value != null && e.key === "Backspace") {
          onChange?.(null);
        }
      }}
      noOptionsText={isLoading ? <Skeleton /> : "Pas de résultat"}
      disabled={disabled}
    />
  );
}

export const PerimetreAutoCompleteWithErrorBoundary =
  withComponentErrorBoundary(PerimetreAutoComplete);
